import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { shouldReduceMotion } from 'src/tef/components/config/animationConfig';
import { ModalBackdrop } from 'src/tef/components/overlay/ModalBackdrop';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';

type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';
type DrawerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface SideDrawerProps extends ForwardedHTMLProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  position?: DrawerPosition;
  size?: DrawerSize;
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  loading?: boolean;
  preventClose?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
}

const horizontalSizeClasses: Record<DrawerSize, string> = {
  xs: 'w-56 max-w-full',
  sm: 'w-72 max-w-full',
  md: 'w-80 max-w-full',
  lg: 'w-96 max-w-full',
  xl: 'w-[28rem] max-w-full',
  full: 'w-screen',
};

const verticalSizeClasses: Record<DrawerSize, string> = {
  xs: 'h-32',
  sm: 'h-48',
  md: 'h-64',
  lg: 'h-80',
  xl: 'h-96',
  full: 'h-screen',
};

const SideDrawerInner = React.forwardRef<HTMLDivElement, SideDrawerProps>(
  (
    {
      isOpen,
      onClose,
      title,
      description,
      children,
      footer,
      position = 'right',
      size = 'md',
      showCloseButton = true,
      closeOnBackdrop = true,
      closeOnEscape = true,
      loading = false,
      preventClose = false,
      initialFocusRef,
      className,
      ...props
    },
    _ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const reduceMotion = shouldReduceMotion();
    const drawerRef = useRef<HTMLDivElement>(null);
    const uniqueId = useId();
    const titleId = `drawer-title-${uniqueId}`;
    const descId = `drawer-desc-${uniqueId}`;

    const canClose = !preventClose && !loading;
    const isHorizontal = position === 'left' || position === 'right';

    const handleClose = useCallback(() => {
      if (canClose) onClose();
    }, [canClose, onClose]);

    const handleBackdropClick = useCallback(() => {
      if (closeOnBackdrop && canClose) onClose();
    }, [closeOnBackdrop, canClose, onClose]);

    useEffect(() => {
      if (isOpen) {
        const focusElement = initialFocusRef?.current || drawerRef.current;
        if (focusElement) {
          setTimeout(() => focusElement.focus(), 0);
        }
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = prevOverflow;
        };
      }
    }, [isOpen, initialFocusRef]);

    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isOpen && closeOnEscape && canClose) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
      }
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, onClose, closeOnEscape, canClose]);

    useEffect(() => {
      if (!isOpen) return;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const drawer = drawerRef.current;
        if (!drawer) return;

        const focusable = drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', handleTab);
      return () => document.removeEventListener('keydown', handleTab);
    }, [isOpen]);

    const getInitialPosition = () => {
      switch (position) {
        case 'left':
          return { x: '-100%' };
        case 'right':
          return { x: '100%' };
        case 'top':
          return { y: '-100%' };
        case 'bottom':
          return { y: '100%' };
      }
    };

    const drawerVariants = reduceMotion
      ? { hidden: getInitialPosition(), visible: { x: 0, y: 0 }, exit: getInitialPosition() }
      : {
          hidden: getInitialPosition(),
          visible: {
            x: 0,
            y: 0,
            transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          },
          exit: {
            ...getInitialPosition(),
            transition: { duration: 0.2 },
          },
        };

    const positionClasses = cn(
      'fixed z-50',
      position === 'left' && 'left-0 top-0 bottom-0',
      position === 'right' && 'right-0 top-0 bottom-0',
      position === 'top' && 'top-0 left-0 right-0',
      position === 'bottom' && 'bottom-0 left-0 right-0',
      isHorizontal ? horizontalSizeClasses[size] : 'w-full',
      !isHorizontal && verticalSizeClasses[size]
    );

    return (
      <>
        <ModalBackdrop isOpen={isOpen} onClick={handleBackdropClick} />
        <AnimatePresence>
          {isOpen && (
            <motion.aside
              ref={drawerRef}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={drawerVariants}
              className={cn(
                positionClasses,
                'bg-white dark:bg-slate-800',
                position === 'right' && 'border-l border-slate-200 dark:border-slate-700',
                position === 'left' && 'border-r border-slate-200 dark:border-slate-700',
                position === 'top' && 'border-b border-slate-200 dark:border-slate-700',
                position === 'bottom' && 'border-t border-slate-200 dark:border-slate-700',
                'shadow-lg',
                'flex flex-col',
                loading && 'pointer-events-none',
                className
              )}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? titleId : undefined}
              aria-describedby={description ? descId : undefined}
              aria-busy={loading}
              tabIndex={-1}
              {...forwardedProps}
              {...props}
            >
              {loading && (
                <span className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
                  <span className="sr-only">Loading...</span>
                </span>
              )}

              {(title || showCloseButton) && (
                <header
                  className={cn(
                    'flex items-start justify-between px-6 py-4',
                    'border-b border-slate-200 dark:border-slate-700'
                  )}
                >
                  <span className="flex-1 min-w-0">
                    {title && (
                      <span
                        id={titleId}
                        className={cn('block', tef.typography.textLg, 'font-semibold', tef.colors.foreground)}
                      >
                        {title}
                      </span>
                    )}
                    {description && (
                      <span
                        id={descId}
                        className={cn('block mt-1', tef.typography.textSm, tef.colors.foregroundSecondary)}
                      >
                        {description}
                      </span>
                    )}
                  </span>
                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={!canClose}
                      className={cn(
                        'p-1.5 rounded-lg ml-4',
                        tef.colors.muted,
                        'hover:bg-slate-100 dark:hover:bg-slate-700',
                        'hover:text-slate-900 dark:hover:text-slate-100',
                        'transition-colors',
                        'focus:outline-none focus:ring-2 focus:ring-primary/50',
                        !canClose && 'opacity-50 cursor-not-allowed'
                      )}
                      aria-label="Close drawer"
                    >
                      <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                  )}
                </header>
              )}

              <section
                className={cn(
                  'flex-1 overflow-y-auto px-6',
                  !title && !showCloseButton && 'pt-6',
                  !footer && 'pb-6',
                  footer && 'pb-4'
                )}
              >
                {children}
              </section>

              {footer && (
                <footer
                  className={cn(
                    'flex items-center justify-end gap-3 px-6 py-4',
                    'border-t border-slate-200 dark:border-slate-700'
                  )}
                >
                  {footer}
                </footer>
              )}
            </motion.aside>
          )}
        </AnimatePresence>
      </>
    );
  }
);

SideDrawerInner.displayName = 'SideDrawer';

export default SideDrawerInner;
export { SideDrawerInner as SideDrawer };
